import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (token) {
      loadProfile();
    } else {
      setLoading(false);
    }
    // No auto-logout: user stays logged in until they manually logout
    // or until they close the browser/tab (session persists via localStorage).
  }, [token]);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      // Trigger background cache refresh for fresh data
      axios.post(`${API}/sync-cache`).catch(() => {});
    } catch (error) {
      console.error('Failed to load profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (readerNumber) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        reader_number: readerNumber,
      });
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      return { success: true };
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        const retryAfter = Number(error.response?.headers?.['retry-after']) || 60;
        return {
          success: false,
          retryAfter,
          message: error.response?.data?.detail || `تجاوزت عدد المحاولات. حاول بعد ${retryAfter} ثانية.`,
        };
      }
      return {
        success: false,
        message: error.response?.data?.detail || 'فشل تسجيل الدخول',
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, refreshProfile: loadProfile, isFullAdmin: user?.admin_role === 'مطلق', isEditor: user?.admin_role === 'محرر', isAdminMember: user?.admin_role === 'عضو إداري' }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
